import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import {
  getCurrentEmployeeProfile,
  hasEmployeeAccess,
} from '../lib/employee-db';
import type {
  EmployeeRole,
  EmployeeProfile,
} from '@tifpa/shared/types/employee-database.types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: EmployeeProfile | null;
  employeeRole: EmployeeRole | null;
  hasEmployeeAccess: boolean;
  loading: boolean;
  profileLoading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [employeeRole, setEmployeeRole] = useState<EmployeeRole | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false); // Track if access has been checked

  // In-flight refresh promise: concurrent refreshProfile() calls await the
  // SAME promise instead of early-returning (an early return let callers
  // proceed before access was actually known)
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  // Track if this is initial load (to distinguish from actual sign-in)
  const isInitialLoadRef = useRef(true);

  const refreshProfile = useCallback((): Promise<void> => {
    // A refresh is already in flight — share it so every caller resolves
    // only once the profile/access check has actually completed
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const run = async (): Promise<void> => {
      // Get current user from Supabase directly
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        setProfile(null);
        setEmployeeRole(null);
        setHasAccess(false);
        setAccessChecked(true);
        setProfileLoading(false);
        return;
      }
      setProfileLoading(true);
      setAccessChecked(false); // Reset when starting to load

      // Safety timeout - don't stay in loading state forever
      const timeoutId = setTimeout(() => {
        console.error('Profile loading timeout - forcing completion');
        setProfileLoading(false);
        // Escape hatch: this attempt is hung, let a fresh refresh start
        if (refreshPromiseRef.current === promise) {
          refreshPromiseRef.current = null;
        }
      }, 10000); // 10 second timeout

      try {
        const [profileData, accessData] = await Promise.all([
          getCurrentEmployeeProfile(),
          hasEmployeeAccess(),
        ]);

        // Only update state if data actually changed to avoid unnecessary re-renders
        setProfile(prev => {
          if (!prev && !profileData) return prev;
          if (!prev || !profileData) return profileData;
          // Deep equality check for profile changes
          if (
            prev.id === profileData.id &&
            prev.name === profileData.name &&
            prev.email === profileData.email &&
            prev.employee_role === profileData.employee_role &&
            prev.avatar_url === profileData.avatar_url &&
            prev.avatar_color === profileData.avatar_color &&
            prev.display_name === profileData.display_name &&
            prev.birthday === profileData.birthday &&
            prev.phone === profileData.phone
          ) {
            return prev; // Return same object reference to prevent re-renders
          }
          return profileData;
        });

        setEmployeeRole(prev => {
          const newRole =
            (profileData?.employee_role as EmployeeRole | null) || null;
          return prev === newRole ? prev : newRole;
        });

        // Set access and mark as checked
        setHasAccess(accessData);
        setAccessChecked(true);
      } catch (error) {
        console.error('Error refreshing profile:', error);
        setProfile(null);
        setEmployeeRole(null);
        setHasAccess(false);
        setAccessChecked(true); // Still mark as checked even on error
      } finally {
        clearTimeout(timeoutId);
        setProfileLoading(false);
      }
    };

    const promise = run().finally(() => {
      if (refreshPromiseRef.current === promise) {
        refreshPromiseRef.current = null;
      }
    });
    refreshPromiseRef.current = promise;
    return promise;
  }, []); // Empty deps since we use getUser() directly

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(async ({ data: { session }, error }) => {
        if (error) {
          console.error('Error getting session:', error);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // If no session, just finish loading
        if (!session) {
          setLoading(false);
        } else {
          // Load profile and check access BEFORE clearing loading
          // But don't hang forever if there's a network error
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Profile load timeout')), 8000)
            );
            await Promise.race([refreshProfile(), timeoutPromise]);
          } catch (err) {
            console.error('Failed to load profile on initial load:', err);
            // Continue anyway - user can still use the app. The refresh may
            // be hung, so schedule ONE retry (after the 10s in-flight safety
            // timeout has released the shared promise) instead of leaving
            // hasAccess=false with no way to recover short of a reload.
            setTimeout(() => {
              void refreshProfile();
            }, 4000);
          } finally {
            setLoading(false);
          }
        }
      })
      .catch(err => {
        console.error('Failed to get session:', err);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignore TOKEN_REFRESHED events - they don't require profile refresh
      // and they can happen frequently causing unnecessary re-renders
      if (event === 'TOKEN_REFRESHED') {
        setSession(session);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN') {
        // Only clear on ACTUAL sign-in, not on page refresh/session restore
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
        // Refresh profile only on actual sign in
        refreshProfile();
      } else if (event === 'SIGNED_OUT') {
        setProfile(null);
        setEmployeeRole(null);
        setHasAccess(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    employeeRole,
    hasEmployeeAccess: hasAccess,
    loading,
    profileLoading,
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
